import React, { useMemo, useState, memo, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Play, TrendingUp, TrendingDown, Minus, Target, CheckCircle, BarChart2, Lightbulb, ChevronDown, ChevronRight, Zap, RefreshCw } from 'lucide-react';
import { type ModuleData, getModuleIcon, getModuleDisplayInfo, getLearningGoal, getFamilyBySubSkill } from '../../content';
import { ModuleProgress } from '../../app/types';
import { Button } from '../ui/Button';
import { ProgressRing, ProgressBar } from '../ui/Progress';
import { useMediaQuery } from 'react-responsive';
import { GroupId } from '../../config/systemConfig';
import { useAppStore } from '../../app/hooks/useAppStore';
import { computeTierProgress, computeSkillMasteries } from '../../mastery';
import { formatSkill } from '../../utils/formatters';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
interface ModulePanelProps {
  isOpen: boolean;
  onClose: () => void;
  module: ModuleData | null;
  progress: ModuleProgress | null;
  onStartPractice: (moduleId: string) => void;
  onViewExplanation: (moduleId: string) => void;
  selectedGroup: GroupId;
}
export const ModulePanel = memo(function ModulePanel({
  isOpen,
  onClose,
  module,
  progress,
  onStartPractice,
  onViewExplanation,
  selectedGroup
}: ModulePanelProps) {
  const isMobile = useMediaQuery({
    maxWidth: 768
  });
  const focusTrapRef = useFocusTrap(isOpen);
  const {
    state,
    getStartingLevel,
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  // Get dynamic content
  const displayInfo = module ? getModuleDisplayInfo(module.id, selectedGroup) : null;
  const title = displayInfo?.title || module?.title;
  const description = displayInfo?.description || module?.description;
  // v10.0: Use group-scoped mastery
  const masteryPercentage = module ? getModuleMasteryForGroup(module.id) : 0;
  // Compute skill-level aggregated masteries
  // FIX 1: Pass state.familyMastery to computeSkillMasteries
  const skillMasteries = useMemo(() => {
    if (!module) return [];
    return computeSkillMasteries(module.id, selectedGroup, state.questionStates, state.familyMastery);
  }, [module, selectedGroup, state.questionStates, state.familyMastery]);
  // FIX 2: Fallback for answered count (handles demo accounts with synthetic question IDs)
  const answeredCount = useMemo(() => {
    if (!module) return 0;
    const baseCount = getAnsweredQuestions(module.id).length;
    if (baseCount > 0) return baseCount;
    // Fallback: sum from skillMasteries (which now derives it from familyMastery)
    return skillMasteries.reduce((sum, skill) => sum + skill.questionsAnswered, 0);
  }, [module, getAnsweredQuestions, skillMasteries]);
  if (!module || !progress) return null;
  const IconComponent = getModuleIcon(module.icon);
  const bgColorMap: Record<string, string> = {
    'text-blue-500': 'bg-blue-50 text-blue-600 border-blue-100',
    'text-indigo-500': 'bg-indigo-50 text-indigo-600 border-indigo-100',
    'text-emerald-500': 'bg-emerald-50 text-emerald-600 border-emerald-100',
    'text-rose-500': 'bg-rose-50 text-rose-600 border-rose-100',
    'text-amber-500': 'bg-amber-50 text-amber-600 border-amber-100',
    'text-purple-500': 'bg-purple-50 text-purple-600 border-purple-100',
    'text-teal-500': 'bg-teal-50 text-teal-600 border-teal-100',
    'text-cyan-500': 'bg-cyan-50 text-cyan-600 border-cyan-100'
  };
  const themeClasses = bgColorMap[module.color] || 'bg-slate-50 text-slate-600 border-slate-100';
  // Compute Tier Progress for "Current Level" display
  const startingTier = getStartingLevel(module.id, selectedGroup) || 1;
  const tierProgressInfo = computeTierProgress(module.id, selectedGroup, state.questionStates, startingTier);
  const currentTier = tierProgressInfo.tierDetails.find(t => t.isCurrent) || tierProgressInfo.tierDetails[0];
  // FIX 3: Use title instead of label for user-friendly display (e.g., "Midden Groep 7" instead of "M7")
  const tierDisplay = currentTier?.title || currentTier?.label || '-';
  // Sort skills: Struggling first, then by lowest mastery
  const sortedSkills = [...skillMasteries].sort((a, b) => {
    if (a.trend === 'struggling' && b.trend !== 'struggling') return -1;
    if (b.trend === 'struggling' && a.trend !== 'struggling') return 1;
    return a.masteryScore - b.masteryScore;
  });
  // Find struggling skill for Smart Tip
  const strugglingSkill = sortedSkills.find(s => s.trend === 'struggling' || s.masteryScore < 35);
  // Find decaying skills for Review Tip
  const decayingSkills = sortedSkills.filter(s => s.hasDecay);
  return <AnimatePresence data-id="element-1460">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-1461" />

          {/* Panel */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="module-panel-title" initial={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} animate={isMobile ? {
        y: 0
      } : {
        x: 0
      }} exit={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className={`
              fixed z-50 bg-[#FAFAF8] shadow-2xl flex flex-col
              ${isMobile ? 'inset-x-0 bottom-0 h-[92vh] rounded-t-[2rem]' : 'inset-y-0 right-0 w-full max-w-md border-l border-white/50'}
            `} data-id="element-1462">
            {/* Mobile Drag Handle */}
            {isMobile && <div className="w-full flex justify-center pt-3 pb-1" onClick={onClose} data-id="element-1463">
                <div className="w-12 h-1.5 bg-slate-300/50 rounded-full" data-id="element-1464" />
              </div>}

            {/* Compact Header */}
            <div className="px-6 py-4 flex items-start justify-between bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-10" data-id="element-1465">
              <div className="flex items-center gap-4 pr-4" data-id="element-1466">
                <div className={`p-3 rounded-2xl ${themeClasses} shadow-sm border`} data-id="element-1467">
                  <IconComponent className="w-7 h-7" strokeWidth={2.5} data-id="element-1468" />
                </div>
                <div data-id="element-1469">
                  <h2 id="module-panel-title" className="text-2xl font-black text-slate-900 tracking-tight leading-tight" data-id="element-1470">
                    {title}
                  </h2>
                  <p className="text-slate-500 font-medium text-sm leading-snug mt-0.5 line-clamp-2" data-id="element-1471">
                    {description}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors shrink-0 mt-1" aria-label="Paneel sluiten" data-id="element-1472">
                <X className="w-5 h-5" data-id="element-1473" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col scroll-smooth" data-id="element-1474">
              <div className="flex flex-col h-full space-y-6" data-id="element-1475">
                {/* 1. Stats Grid */}
                <div className="grid grid-cols-2 gap-3" data-id="element-1476">
                  {/* Mastery */}
                  <div className="col-span-2 bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm flex items-center gap-4" data-id="element-1477">
                    <div className="shrink-0" data-id="element-1478">
                      <ProgressRing progress={masteryPercentage} size={64} strokeWidth={6} showLabel={false} color={masteryPercentage >= 95 ? 'text-emerald-500' : masteryPercentage > 0 ? 'text-orange-500' : 'text-slate-200'} data-id="element-1479" />
                    </div>
                    <div data-id="element-1480">
                      <div className="text-3xl font-black text-slate-900" data-id="element-1481">
                        {masteryPercentage > 0 ? `${Math.round(masteryPercentage)}%` : '0%'}
                      </div>
                      <div className="text-sm text-slate-500 font-bold uppercase tracking-wider" data-id="element-1482">
                        Totale Beheersing
                      </div>
                    </div>
                  </div>

                  {/* Level */}
                  <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm flex flex-col justify-center" data-id="element-1483">
                    <div className="flex items-center gap-2 mb-1" data-id="element-1484">
                      <Target className="w-4 h-4 text-orange-500" data-id="element-1485" />
                      <div className="text-xs text-slate-500 font-bold uppercase tracking-wider" data-id="element-1486">
                        Niveau
                      </div>
                    </div>
                    <div className="text-lg font-black text-slate-900 leading-tight" data-id="element-1487">
                      {tierDisplay}
                    </div>
                  </div>

                  {/* Answered */}
                  <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm flex flex-col justify-center" data-id="element-1488">
                    <div className="flex items-center gap-2 mb-1" data-id="element-1489">
                      <CheckCircle className="w-4 h-4 text-emerald-500" data-id="element-1490" />
                      <div className="text-xs text-slate-500 font-bold uppercase tracking-wider" data-id="element-1491">
                        Gemaakt
                      </div>
                    </div>
                    <div className="text-lg font-black text-slate-900 leading-tight" data-id="element-1492">
                      {answeredCount}{' '}
                      <span className="text-sm font-medium text-slate-500 normal-case" data-id="element-1493">
                        vragen
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2. Smart Tip */}
                {strugglingSkill && <div className="bg-amber-50 border border-amber-200/60 rounded-2xl p-4 flex gap-3 shadow-sm" data-id="element-1494">
                    <div className="shrink-0 mt-0.5" data-id="element-1495">
                      <div className="bg-white p-2 rounded-xl shadow-sm text-amber-500 border border-amber-100" data-id="element-1496">
                        <Lightbulb className="w-5 h-5" data-id="element-1497" />
                      </div>
                    </div>
                    <div data-id="element-1498">
                      <h4 className="font-bold text-amber-900 text-sm mb-0.5" data-id="element-1499">
                        Focus Tip
                      </h4>
                      <p className="text-sm text-amber-800/80 font-medium leading-snug" data-id="element-1500">
                        <span className="font-bold capitalize text-amber-900" data-id="element-1501">
                          {formatSkill(strugglingSkill.skill)}
                        </span>{' '}
                        kan nog wat extra aandacht gebruiken.
                      </p>
                    </div>
                  </div>}

                {/* 2b. Review Tip (Decay) */}
                {decayingSkills.length > 0 && <div className="bg-teal-50 border border-teal-200/60 rounded-2xl p-4 flex gap-3 shadow-sm" data-id="element-1502">
                    <div className="shrink-0 mt-0.5" data-id="element-1503">
                      <div className="bg-white p-2 rounded-xl shadow-sm text-teal-500 border border-teal-100" data-id="element-1504">
                        <RefreshCw className="w-5 h-5" data-id="element-1505" />
                      </div>
                    </div>
                    <div data-id="element-1506">
                      <h4 className="font-bold text-teal-900 text-sm mb-0.5" data-id="element-1507">
                        Herhaling Nodig
                      </h4>
                      <p className="text-sm text-teal-800/80 font-medium leading-snug" data-id="element-1508">
                        Je hebt{' '}
                        <strong className="text-teal-900" data-id="element-1509">
                          {decayingSkills.length}{' '}
                          {decayingSkills.length === 1 ? 'vaardigheid' : 'vaardigheden'}
                        </strong>{' '}
                        al een tijdje niet geoefend.
                      </p>
                    </div>
                  </div>}

                {/* 3. Skill Mastery Section */}
                <div data-id="element-1510">
                  <div className="flex items-center gap-2 mb-4" data-id="element-1511">
                    <BarChart2 className="w-5 h-5 text-slate-400" data-id="element-1512" />
                    <h3 className="text-lg font-black text-slate-800" data-id="element-1513">
                      Jouw Vaardigheden
                    </h3>
                  </div>

                  {sortedSkills.length > 0 ? <div className="space-y-3" data-id="element-1514">
                      {sortedSkills.map(skill => {
                  const isMastered = skill.masteryScore >= 85;
                  const isGood = skill.masteryScore >= 50;
                  const isExpanded = expandedSkill === skill.skill;
                  const hasMultipleSubSkills = skill.subSkills.length > 1;
                  let colorClass = 'bg-amber-500';
                  let bgClass = 'bg-white border-slate-200/60';
                  let textClass = 'text-slate-700';
                  let scoreClass = 'text-amber-600 bg-amber-50';
                  if (isMastered) {
                    colorClass = 'bg-emerald-500';
                    textClass = 'text-slate-800';
                    scoreClass = 'text-emerald-700 bg-emerald-50';
                  } else if (isGood) {
                    colorClass = 'bg-orange-500';
                    textClass = 'text-slate-800';
                    scoreClass = 'text-orange-700 bg-orange-50';
                  }
                  return <div key={skill.skill} className={`rounded-xl border ${bgClass} shadow-sm overflow-hidden transition-all`} data-id="element-1515">
                            {/* Skill Row */}
                            <div className={`p-3.5 flex items-center justify-between gap-3 ${hasMultipleSubSkills ? 'cursor-pointer hover:bg-slate-50/50' : ''}`} onClick={() => {
                      if (hasMultipleSubSkills) {
                        setExpandedSkill(isExpanded ? null : skill.skill);
                      }
                    }} data-id="element-1516">
                              <div className="flex-1" data-id="element-1517">
                                <div className="flex items-center justify-between mb-2" data-id="element-1518">
                                  <div className="flex items-center gap-2" data-id="element-1519">
                                    {hasMultipleSubSkills && <div className="text-slate-400" data-id="element-1520">
                                        {isExpanded ? <ChevronDown className="w-4 h-4" data-id="element-1521" /> : <ChevronRight className="w-4 h-4" data-id="element-1522" />}
                                      </div>}
                                    <span className={`font-bold text-sm capitalize ${textClass}`} data-id="element-1523">
                                      {formatSkill(skill.skill)}
                                    </span>
                                    {skill.hasDecay && <div className="bg-teal-100 text-teal-700 p-1 rounded-full" title="Herhaling nodig" data-id="element-1524">
                                        <RefreshCw className="w-3 h-3" data-id="element-1525" />
                                      </div>}
                                  </div>

                                  <div className="flex items-center gap-2" data-id="element-1526">
                                    {skill.trend === 'improving' && <TrendingUp className="w-3.5 h-3.5 text-emerald-500" data-id="element-1527" />}
                                    {skill.trend === 'struggling' && <TrendingDown className="w-3.5 h-3.5 text-amber-500" data-id="element-1528" />}
                                    {skill.trend === 'stable' && <Minus className="w-3.5 h-3.5 text-slate-300" data-id="element-1529" />}
                                    <span className={`text-xs font-black px-1.5 py-0.5 rounded-md ${scoreClass}`} data-id="element-1530">
                                      {skill.masteryScore}%
                                    </span>
                                  </div>
                                </div>
                                <ProgressBar value={skill.masteryScore} height={4} color={colorClass} className="bg-slate-100" data-id="element-1531" />
                              </div>
                            </div>

                            {/* SubSkill Drill-Down */}
                            <AnimatePresence data-id="element-1532">
                              {isExpanded && hasMultipleSubSkills && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-hidden bg-slate-50/80 border-t border-slate-100" data-id="element-1533">
                                  <div className="p-2 space-y-1.5" data-id="element-1534">
                                    {skill.subSkills.sort((a, b) => a.masteryScore - b.masteryScore).map(sub => {
                            const family = getFamilyBySubSkill(sub.subSkill);
                            const learningGoal = family ? getLearningGoal(family.familyId) : '';
                            return <div key={sub.subSkill} className="flex flex-col bg-white rounded-lg px-3 py-2 border border-slate-100 shadow-sm" data-id="element-1535">
                                            <div className="flex items-center justify-between" data-id="element-1536">
                                              <span className="text-xs font-medium text-slate-600 capitalize" data-id="element-1537">
                                                {formatSkill(sub.subSkill)}
                                              </span>
                                              <div className="flex items-center gap-2" data-id="element-1538">
                                                {sub.hasDecay && <RefreshCw className="w-3 h-3 text-teal-500" title="Herhaling nodig" data-id="element-1539" />}
                                                <span className="text-xs font-bold text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded" data-id="element-1540">
                                                  {sub.masteryScore}%
                                                </span>
                                              </div>
                                            </div>
                                            {learningGoal && <div className="text-[11px] text-slate-400 font-normal mt-0.5 flex items-start gap-1" data-id="element-1541">
                                                <Target className="w-3 h-3 shrink-0 mt-[1px] text-slate-300" data-id="element-1542" />
                                                <span data-id="element-1543">{learningGoal}</span>
                                              </div>}
                                          </div>;
                          })}
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                })}
                    </div> : <div className="text-center py-8 bg-white rounded-2xl border border-dashed border-slate-300" data-id="element-1544">
                      <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-1545">
                        <Zap className="w-5 h-5 text-slate-400" data-id="element-1546" />
                      </div>
                      <p className="text-slate-500 font-medium text-sm" data-id="element-1547">
                        Start met oefenen om je vaardigheden te zien!
                      </p>
                    </div>}
                </div>

                {/* Bottom padding for sticky actions */}
                <div className="h-24 shrink-0" data-id="element-1548" />
              </div>
            </div>

            {/* Sticky Bottom Actions */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#FAFAF8] via-[#FAFAF8] to-transparent pt-12 pointer-events-none" data-id="element-1549">
              <div className="flex flex-col gap-3 pointer-events-auto" data-id="element-1550">
                <Button onClick={() => onStartPractice(module.id)} size="xl" fullWidth className="shadow-lg shadow-orange-200/50 h-14 text-base group" data-id="element-1551">
                  <span className="font-black mr-2" data-id="element-1552">Start Oefensessie</span>
                  <div className="bg-white/20 rounded-md p-1 group-hover:bg-white/30 transition-colors" data-id="element-1553">
                    <Play className="w-4 h-4 fill-current" data-id="element-1554" />
                  </div>
                </Button>

                <Button onClick={() => onViewExplanation(module.id)} variant="ghost" size="lg" fullWidth className="text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 h-12" data-id="element-1555">
                  <BookOpen className="w-4 h-4 mr-2" data-id="element-1556" />
                  <span className="font-bold" data-id="element-1557">Bekijk Uitleg</span>
                </Button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
});